import { stringify } from 'querystring';
import React from 'react';
import { useRouter } from 'next/router';
import { Grid } from '@havenengineering/module-shared-library';
import ErrorComponent from '@havenengineering/module-shared-library/dist/components/Error';
import Head from 'next/head';
import { analytics } from '../../helpers/gtm/analytics';

import Section from '../../module/aggregating-by-grade/component/section';
import {
  getPreviousStepWithoutSelection,
  getReferringStepDetails,
  getStep,
  shouldSkipStep,
} from '../../helpers/redirects';
import {
  AggregatedAccommodationResponse,
  AggregatedAccommodationType,
} from '../../module/aggregating-by-grade/type/types';
import { getDataLayerPageName, getGradeResultsAvailability } from '../../helpers/gtm/gtm-shared';
import { loggerHaven } from '../../helpers/logger/logger-haven';
import { getJourneyStepsSsr } from '../../lib/server-side-functions';
import {
  addAccommodationFeaturesToResults,
  getAccommodationTypeOccupancyScore,
} from '../../helpers/aggregating/accommodation';
import { getHolidaysHeaderTitle } from '../../module/shared/helper/get-holidays-header-title';
import { sharedProps } from '../../middleware/sharedServerSideProps';
import { getSortOrder } from '../../helpers/gtm/gtm-aggregating-journey';
import { PartnerPricingBanner } from '../../module/shared/component/partner-pricing-banner/partner-pricing-banner';
import { getAffiliatePromoCookie } from '../../helpers/affiliatePromoCookie';
import { fetchLeadingBreaksData, LeadingBreakData } from '../../helpers/aggregating/leading-breaks';
import { AggregatingSearchHeader } from '../../module/shared/component/aggregating-search-header';
import { useFetchAffiliateData } from '../../hooks/useFetchAffiliateData';
import { handleApiError } from '../../helpers/error-handler';
import { useUnleash } from '../../providers/unleashProvider';
import { getAccommodationFeatures } from '../../api/v2/search';
import { GroupedAccommodationFeatures } from '../../types/accommodation-features';

const accommodationTypeReverseOrder = ['lodge', 'caravan'];

const accommodationSubTypeReverseOrder = [
  'lodge_signature',
  'lodge_diamond_hot_tub',
  'lodge_diamond',
  'lodge_emerald',
  'caravan_signature_gold',
  'caravan_gold',
  'caravan_signature',
  'caravan_silver',
  'caravan_bronze',
  'caravan_saver',
];

export const Home = ({
  items,
  accommodationTypeOrder,
  hasError = false,
  errorCode,
  interactionProps,
}: AggregatedAccommodationResponse) => {
  const { query } = useRouter();

  const { isUnleashVariantActiveCsr } = useUnleash();

  const { isPromoProcessingCompleteWithoutErrors, promoCode } = useFetchAffiliateData(
    query?.wacampaign as string,
  );

  const partnerPricingEnabled = isUnleashVariantActiveCsr(
    'search-0387-partner-pricing',
    '1',
    query,
  );

  if (hasError) {
    return (
      <ErrorComponent
        statusCode={errorCode}
        analytics={(dataLayer) => analytics(dataLayer, false)}
        customLinkProps={{ href: 'https://www.haven.com' }}
      />
    );
  }

  const holidaysHeaderTitle = getHolidaysHeaderTitle({
    arrivalDate: (query?.arrivalDate || '') as string,
    adults: (query.adults || '') as string,
    children: (query.children || '') as string,
    tots: (query.tots || '') as string,
    dogs: (query.dogs || '') as string,
    duration: (query.duration || '') as string,
    parkCode: (query.parkCode || '') as string,
    packageType: (query.packageType || '') as string,
  });
  const dataLayerPageName = getDataLayerPageName('grades');

  const isBlueLightPartner = promoCode === 'EMB_BLUELIGHT';
  const showPartnerPricingBanner = isBlueLightPartner && partnerPricingEnabled;

  const bannerText = showPartnerPricingBanner
    ? 'Discount code applied'
    : 'Discount code applied - eligible savings at checkout';

  const finalBannerText = isPromoProcessingCompleteWithoutErrors ? bannerText : null;

  return (
    <div className="holiday-search-container">
      <Head>
        <title>{holidaysHeaderTitle}</title>
      </Head>
      {finalBannerText && <PartnerPricingBanner bannerText={finalBannerText} />}
      <AggregatingSearchHeader activeStep="Accommodation" showShortlistingButton />
      <Grid.Container>
        <Grid.Row justify="center">
          <Section
            resultsByAccommodationType={items}
            query={query}
            dataLayerPageName={dataLayerPageName}
            accommodationTypeOrder={accommodationTypeOrder}
            bannerText={bannerText}
            interactionProps={interactionProps}
          />
        </Grid.Row>
      </Grid.Container>
    </div>
  );
};

export const getServerSideProps = sharedProps(async (context, isUnleashVariantActiveSsr) => {
  let journeySteps;

  try {
    const dogs = context.query.dogs ? parseInt(context.query.dogs as string, 10) : 0;

    const packagesFirstExperiment = isUnleashVariantActiveSsr('search-0434-widget', '2');

    const v4InventoryEnabled = isUnleashVariantActiveSsr('search-v4-inventory-toggle', '1');

    journeySteps = await getJourneyStepsSsr(packagesFirstExperiment);

    const { referringStepPathname, newReferringStepName } = getReferringStepDetails(
      journeySteps,
      context.req.url || '',
      context.query.referringStep as string,
    );

    const queryString = stringify({
      ...context.query,
      referringStep: newReferringStepName,
    });

    const currentStep = getStep(journeySteps, context.req.url || '');

    if (currentStep.pathName === '') {
      return {
        redirect: {
          destination: `${journeySteps[0].pathName}?${queryString}`,
          permanent: true,
        },
      };
    }

    const { previousSelectionsMissing, stepPathName: previousStepPathName } =
      getPreviousStepWithoutSelection(journeySteps, context.req.url || '', context.query);

    if (previousSelectionsMissing) {
      return {
        redirect: {
          destination: `${previousStepPathName}?${queryString}`,
          permanent: true,
        },
      };
    }

    const { wacampaign, ...restQuery } = context.query;

    const partnerPricingEnabled = isUnleashVariantActiveSsr('search-0387-partner-pricing', '1');
    const accommodationFeaturesFromSearchApiOn = isUnleashVariantActiveSsr(
      'search-accommodation-features-endpoint',
      '1',
    );
    const includeDogFriendlyEnabled = isUnleashVariantActiveSsr(
      'search-0415-show-dog-and-non-dog-mixed',
      '1',
    );

    const affiliatePromo = context.req.cookies.AffiliatePromo;

    const { affiliatePromoCode } = getAffiliatePromoCookie(
      wacampaign as string | undefined,
      affiliatePromo,
      partnerPricingEnabled,
    );

    const responseItems = (await fetchLeadingBreaksData(
      journeySteps,
      currentStep,
      {
        ...restQuery,
        ...(affiliatePromoCode ? { wacampaign: affiliatePromoCode } : { wacampaign: undefined }),
      },
      undefined,
      ['accommodationType', 'baseGradeCode'],
      [
        'parkCode',
        'nestedOccupancy',
        'soldOuts',
        'promotionData',
        'accommodationDescription',
        'baseGradeCode',
        'accommodationSubType',
        'grade',
        'floorPlan',
        'matterPortId',
        'caravanFeatures',
        'caravanImages',
        'parkName',
        'key',
        'dogFriendly',
        'sleepingArrangements',
        'pending',
        'financeOption',
        ...(v4InventoryEnabled ? ['minDeposit'] : []),
      ],
      v4InventoryEnabled,
    )) as AggregatedAccommodationType[];

    const responseItemsDogs = (await fetchLeadingBreaksData(
      journeySteps,
      currentStep,
      {
        ...restQuery,
        includeDogFriendly: 'true',
        ...(affiliatePromoCode ? { wacampaign: affiliatePromoCode } : { wacampaign: undefined }),
      },
      undefined,
      ['accommodationType', 'baseGradeCode'],
      [
        'parkCode',
        'nestedOccupancy',
        'soldOuts',
        'promotionData',
        'accommodationDescription',
        'baseGradeCode',
        'accommodationSubType',
        'grade',
        'floorPlan',
        'matterPortId',
        'caravanFeatures',
        'caravanImages',
        'parkName',
        'key',
        'dogFriendly',
        'sleepingArrangements',
        'pending',
        'financeOption',
        ...(v4InventoryEnabled ? ['minDeposit'] : []),
      ],
      v4InventoryEnabled,
    )) as AggregatedAccommodationType[];

    const items = dogs === 0 && !includeDogFriendlyEnabled ? responseItems : responseItemsDogs;

    if (!items?.length) {
      loggerHaven.warn(context, 'NO_RESULTS_ACCOMMODATION', { ...context.query });
      context.res.statusCode = 404;
    }

    const sortAccommodationTypes = (
      a: AggregatedAccommodationType,
      b: AggregatedAccommodationType,
    ) => {
      if (!a.accommodationType || !b.accommodationType) return 0;

      return (
        accommodationTypeReverseOrder.indexOf(b.accommodationType.toLowerCase()) -
        accommodationTypeReverseOrder.indexOf(a.accommodationType.toLowerCase())
      );
    };

    const sortAccommodationSubTypes = (
      a: AggregatedAccommodationType,
      b: AggregatedAccommodationType,
    ) => {
      if (!a.accommodationSubType || !b.accommodationSubType) return 0;

      return (
        accommodationSubTypeReverseOrder.indexOf(b.accommodationSubType) -
        accommodationSubTypeReverseOrder.indexOf(a.accommodationSubType)
      );
    };

    const reorderDogFriendlyGrades = (results: AggregatedAccommodationType[]) =>
      results.map((result) => {
        const grouped = result.subItems.reduce<Record<string, LeadingBreakData[]>>((acc, item) => {
          (acc[item.accommodationSubType] ||= []).push({
            ...item,
            dogFriendly:
              typeof item.dogFriendly === 'string' ? item.dogFriendly === 'true' : item.dogFriendly,
          });
          return acc;
        }, {});

        return {
          ...result,
          subItems: Object.values(grouped).flatMap((group) =>
            group.sort((a, b) => Number(a.dogFriendly) - Number(b.dogFriendly)),
          ),
        };
      });

    const responseItemsSorted = items
      .map((accommodationType) => ({
        ...accommodationType,
        subItems: accommodationType.subItems?.sort(sortAccommodationSubTypes),
      }))
      .sort(sortAccommodationTypes);

    const largestPhysicalUnits = Math.max(
      ...responseItemsSorted.map(({ occupancy }) =>
        occupancy?.physicalUnits ? occupancy?.physicalUnits : 0,
      ),
    );

    const accommodationTypeWithOccupancyScore = responseItemsSorted.map((accommodationType) => ({
      ...accommodationType,
      ...(accommodationType.occupancy
        ? {
            occupancy: {
              ...accommodationType.occupancy,
              score:
                getAccommodationTypeOccupancyScore(
                  accommodationType.occupancy,
                  largestPhysicalUnits,
                  context.query?.occupancyMultiplier as string | undefined,
                ) || 0,
            },
          }
        : {}),
    }));

    const sortedAccommodationType = accommodationTypeWithOccupancyScore.sort((a, b) => {
      if (!a.occupancy?.score || !b.occupancy?.score) {
        return 0;
      }

      return b.occupancy.score >= 0 && a.occupancy.score >= 0
        ? b.occupancy.score - a.occupancy.score
        : -1;
    });

    const sortedAccommodationTypeOrderString = getSortOrder('grade', sortedAccommodationType);

    const resultsByAccommodation: AggregatedAccommodationType[] = sortedAccommodationType;

    let results;

    try {
      const accommodationFeatures = accommodationFeaturesFromSearchApiOn
        ? await getAccommodationFeatures({
            groupBy: 'grade',
            parkCode: (restQuery?.parkCode as string)?.toLowerCase(),
          })
        : {};

      const resultsByAccommodationWithFeatures = accommodationFeaturesFromSearchApiOn
        ? addAccommodationFeaturesToResults(
            resultsByAccommodation,
            accommodationFeatures as GroupedAccommodationFeatures,
          )
        : resultsByAccommodation;

      results = resultsByAccommodationWithFeatures;
    } catch (err) {
      loggerHaven.error(context, 'GRADES_ACCOMMODATION_FEATURES', { ...context.query }, err);

      results = resultsByAccommodation;
    }

    const newOrder = reorderDogFriendlyGrades(results);

    const interactionProps = getGradeResultsAvailability(responseItemsDogs);

    const props = {
      items: newOrder,
      accommodationTypeOrder: sortedAccommodationTypeOrderString,
      interactionProps,
    };

    // amend query string if there is one accommodation grade available, so we only send through the wildcard of the grade that is available
    if (resultsByAccommodation.length === 1) {
      const selectedGradeCode = resultsByAccommodation[0]?.baseGradeCode;
      const selectedGradeName = resultsByAccommodation[0]?.accommodationDescription;

      context.query = {
        ...context.query,
        selectedGradeCode,
        selectedGradeName,
      };
    }

    const shouldSkipStepRedirect = shouldSkipStep(
      journeySteps,
      context.req.url || '',
      referringStepPathname || '',
      queryString,
      resultsByAccommodation.length,
    );

    return {
      redirect: shouldSkipStepRedirect,
      props,
    };
  } catch (err) {
    loggerHaven.error(context, 'GRADES_SSR_COMPLETE', { ...context.query }, err);

    return handleApiError(err, journeySteps);
  }
});

export default Home;
